import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  inject,
  input,
  OnInit,
  signal
} from "@angular/core";
import {CommonModule} from "@angular/common";
import {MatTableDataSource, MatTableModule} from "@angular/material/table";
import {MatCardModule} from "@angular/material/card";
import {MatMenuModule} from "@angular/material/menu";
import {MatProgressSpinner} from "@angular/material/progress-spinner";
import {LatLng} from "leaflet";
import {combineLatest, forkJoin, switchMap} from "rxjs";
import {TranslocoPipe} from "@jsverse/transloco";
import {LOREN_DATE} from "../../../../shared/constants/loren-date.constants";
import {
  LorenTrackingReportGridViewModel
} from "../loren-asset-reports/interfaces/loren-tracking-report-grid-view-model.interface";
import {
  LorenAssetTrackingTableDataSource
} from "../loren-asset-reports/interfaces/loren-asset-tracking-table-data-source.interface";
import {GenericItems} from "../../../../shared/interfaces/generic-items.interface";
import {LorenTrace} from "../../../loren-traces/interfaces/loren-trace.interface";
import {LorenSnackBarService} from "../../../../shared/services/loren-snack-bar-service/loren-snack-bar.service";
import {LorenImageModule} from "../../../../shared/pipes/loren-image/loren-image.module";
import {LorenBaseMapModule} from "../../../../shared/modules/loren-base-map/loren-base-map.module";
import {LorenDateModule} from "../../../../shared/pipes/loren-date/loren-date.module";
import {
  LorenAssetReportTypeService
} from "../loren-asset-reports/services/loren-asset-report-type-service/loren-asset-report-type.service";
import {LorenMetering} from "../../../loren-assets/interfaces/loren-metering.interface";
import {DateRange} from "../../../../common/forms/period-select/period-select.component";
import {takeUntilDestroyed, toObservable} from "@angular/core/rxjs-interop";
import {RefreshSubject} from "../../../../common/rxjs/RefreshSubject";

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    MatCardModule,
    MatTableModule,
    MatProgressSpinner,
    MatMenuModule,
    TranslocoPipe,
    LorenBaseMapModule,
    LorenImageModule,
    LorenDateModule,
  ],
  selector: "loren-tracking-report",
  styleUrls: ["./loren-tracking-report.component.scss"],
  templateUrl: "./loren-tracking-report.component.html",
})
export class LorenTrackingReportComponent implements OnInit {
  private readonly destroyRef = inject(DestroyRef);
  private snackBarService = inject(LorenSnackBarService);
  private reportTypeService = inject(LorenAssetReportTypeService);

  meterings = input<LorenMetering[]>();
  image = input<string>();

  readonly dateFormats = LOREN_DATE;
  private trackingData = signal<GenericItems<LorenTrace>[]>([]);

  error = signal<boolean>(false);
  loading = signal<boolean>(false);
  trace = signal<LatLng[]>([]);
  trackingReports = signal<LorenTrackingReportGridViewModel[]>([]);
  refresh$ = input.required<RefreshSubject>()

  period = input.required<DateRange>();

  dataSource = signal<MatTableDataSource<LorenAssetTrackingTableDataSource> | null>(null);

  private readonly meterings$ = toObservable(this.meterings);
  private readonly period$ = toObservable(this.period);

  ngOnInit() {
    this.loading.set(true);
    combineLatest([
      this.meterings$,
      this.period$,
      this.refresh$(),
    ]).pipe(
      takeUntilDestroyed(this.destroyRef),
      switchMap(([meterings, period]) => {
        return forkJoin(
          meterings.map((metering) =>
            this.reportTypeService.getTrackingHistoryForMetering(
              metering.id,
              period
            )
          )
        )
      })
    ).subscribe({
      next: (res) => {
        this.error.set(false);
        this.loading.set(false);
        this.trackingData.set(res);
        this.prepareTrackingStats();
        const dataSource = this.dataSource();
        if (dataSource) {
          this.trace.set(dataSource.data.map((x) => x.position));
        }
      },
      error: () => {
        this.error.set(true);
        this.loading.set(false);
      },
    })
  }

  private prepareTrackingStats() {
    this.trackingReports.set([]);
    const traceData: LorenAssetTrackingTableDataSource[] = [];
    this.dataSource.set(null);

    if (this.trackingData().some((x) => x.items.length > 0) === false) {
      this.snackBarService.showNoDataAvailableForAsset();
      return;
    }

    this.trackingData().forEach((x) => {
      const assetTrace = x.items;

      traceData.push(
        ...assetTrace.map((s) => {
          return {
            time: s.time,
            position: new LatLng(s.latitude, s.longitude),
          };
        })
      );

      if (traceData.length === 0) {
        return;
      }

      this.trackingReports.update((v) => [
        ...v,
        {
          startLocation: traceData[0].position,
          avatar: this.image(),
          displayedColumns: ["time", "lat", "lng", "position"],
        },
      ]);
    });

    this.dataSource.set(
      new MatTableDataSource<LorenAssetTrackingTableDataSource>(traceData)
    );
  }
}
